import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNotification } from "../common/NotificationContext";
import {
  getFirestore,
  collection,
  getDocs,
  writeBatch,
  doc,
  getDoc,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faDownload, faSearch } from "@fortawesome/free-solid-svg-icons";
import { InputField, Button } from "@cakrawalajakarta/shared-components";
import Papa from "papaparse";

// Fungsi untuk mengkapitalkan huruf pertama setiap kata
const capitalizeWords = (str) => {
  if (!str || typeof str !== "string") return "";
  return str
    .trim()
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Fungsi untuk mengonversi waktu HH:MM:SS ke detik untuk sorting numerik
const timeToSeconds = (time) => {
  if (!time) return Infinity;
  const [h, m, s] = time.split(":").map(Number);
  return h * 3600 + m * 60 + s;
};

// Komponen untuk baris skeleton saat loading
const SkeletonRow = React.memo(() => (
  <tr className="border-b border-gray-200">
    {Array(9)
      .fill()
      .map((_, index) => (
        <td key={index} className="px-4 py-4">
          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
        </td>
      ))}
  </tr>
));

// Komponen untuk tombol kategori
const CategoryButton = React.memo(({ category, selectedCategory, onClick }) => (
  <button
    onClick={() => onClick(category.distance)}
    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
      selectedCategory === category.distance
        ? "bg-red-600 text-white"
        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
    }`}
    aria-label={`Filter hasil berdasarkan kategori ${category.displayName}`}
  >
    {category.displayName}
  </button>
));

// Komponen untuk tabel top 3
const Top3Table = ({ title, results, isMixGroup }) => (
  <div className="w-full md:w-[calc(50%-0.5rem)] bg-white rounded-xl border border-gray-300 overflow-x-auto">
    <h2 className="text-lg font-semibold text-red-600 mb-4 px-4 pt-4">
      {title}
    </h2>
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Peringkat
          </th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {isMixGroup ? "Nama Tim" : "Nama Lengkap"}
          </th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Nomor Bib
          </th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Durasi
          </th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Kategori
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {results.length === 0 ? (
          <tr>
            <td
              colSpan={5}
              className="px-4 py-2 text-center text-sm text-gray-600"
            >
              Tidak ada data tersedia
            </td>
          </tr>
        ) : (
          results.map((result, index) => (
            <tr
              key={`top3-${result.id || index}`}
              className="hover:bg-gray-50 transition-colors"
            >
              <td className="px-4 py-2 text-sm text-gray-600">
                {result.genderRanking || result.ranking}
              </td>
              <td className="px-4 py-2 text-sm text-gray-600">
                {isMixGroup
                  ? capitalizeWords(result.teamName) || "Tim Tidak Diketahui"
                  : capitalizeWords(
                      `${result.firstName} ${result.familyName || ""}`
                    ) || "-"}
              </td>
              <td className="px-4 py-2 text-sm text-gray-600">
                {result.bibNumber || "-"}
              </td>
              <td className="px-4 py-2 text-sm text-gray-600">
                {result.time || "-"}
              </td>
              <td className="px-4 py-2 text-sm text-gray-600">
                {result.categoryName || "-"}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

export const RaceResultsUpload = () => {
  const { showNotification } = useNotification();
  const [state, setState] = useState({
    raceResults: [],
    top3Male: [],
    top3Female: [],
    loading: true,
    totalResults: 0,
    selectedCategory: "7-vertical",
    categories: [],
    categoriesLoaded: false,
    selectedDistance: null,
    bibNumberCache: new Map(),
    searchQuery: "",
  });
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const [csvWarnings, setCsvWarnings] = useState([]);
  const [csvSuccessCount, setCsvSuccessCount] = useState(0);
  const [isProcessingCsv, setIsProcessingCsv] = useState(false);
  const [csvFileName, setCsvFileName] = useState("");
  const [skipNameValidation, setSkipNameValidation] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [clearExistingResults, setClearExistingResults] = useState(false);

  const db = getFirestore();
  const auth = getAuth();
  const navigate = useNavigate();
  const isMounted = useRef(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthUser(user);
      setAuthLoading(false);
      if (!user) {
        showNotification(
          "error",
          "Silakan login untuk mengunggah hasil perlombaan."
        );
        navigate("/login");
      }
    });
    return () => {
      unsubscribe();
    };
  }, [auth, navigate, showNotification]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const mapDistanceFromRegistration = (regDistance, categoryNameFromReg) => {
    const dist = String(regDistance).trim();
    if (dist === "7") {
      if (categoryNameFromReg?.includes("Vertical")) return "7-vertical";
      if (categoryNameFromReg?.includes("Fun Trail")) return "7-fun";
      return "7-vertical";
    } else if (dist === "20") {
      if (categoryNameFromReg?.includes("Classic Distance"))
        return "20-classic";
      if (categoryNameFromReg?.includes("Mix Group")) return "20-mix";
      return "20-classic";
    }
    return dist;
  };

  const clearExistingRaceResults = async (distance) => {
    if (!distance) {
      showNotification("error", "Silakan pilih kategori sebelum menghapus.");
      return;
    }
    try {
      const subCollectionRef = collection(
        db,
        "resultsUltra24",
        distance,
        "results"
      );
      const resultsSnapshot = await getDocs(subCollectionRef);
      const batch = writeBatch(db);
      resultsSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
      showNotification(
        "success",
        `Berhasil menghapus hasil lama untuk kategori ${distance}.`
      );
    } catch (err) {
      console.error("[ERROR] Failed to clear existing results:", err);
      showNotification("error", "Gagal menghapus hasil lama.");
    }
  };

  const fetchAllBibNumbers = async () => {
    try {
      const participantsCollection = collection(db, "pendaftaranUltra24");
      const participantsSnapshot = await getDocs(participantsCollection);
      const bibNumberCache = new Map();
      participantsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.kategori === "Mix Group (20K)") {
          const bibNumbers =
            data.participants?.map((p) => p.bibNumber || "-").join(", ") || "-";
          bibNumberCache.set(doc.id, {
            bibNumber: bibNumbers,
            teamName: capitalizeWords(data.teamName) || "-",
          });
        } else {
          bibNumberCache.set(doc.id, {
            bibNumber: data.bibNumber || "-",
            teamName: null,
          });
        }
      });
      return bibNumberCache;
    } catch (err) {
      console.error("[ERROR] Failed to fetch bibNumbers:", err);
      showNotification("error", "Gagal memuat nomor bib.");
      return new Map();
    }
  };

  const fetchCategories = useCallback(async () => {
    try {
      const eventRef = doc(db, "semuaKegiatan", "ULTRA24");
      const eventDoc = await getDoc(eventRef);
      if (eventDoc.exists()) {
        const rawCategories = eventDoc.data().categories || [];
        const mappedCategories = [];
        const seenDistances = new Set();

        rawCategories.forEach((cat) => {
          let distance = cat.distance.toString().trim();
          let displayName = cat.categoryName;
          let categoryName = cat.categoryName;
          let description = cat.description || cat.categoryName;

          if (cat.categoryName === "Vertical (7K)") {
            distance = "7-vertical";
          } else if (cat.categoryName === "Fun Trail (7K)") {
            distance = "7-fun";
          } else if (cat.categoryName === "Classic Distance (20K)") {
            distance = "20-classic";
            displayName = "Classic Distance (20K)";
            categoryName = "Classic Distance (20K)";
          } else if (cat.categoryName === "Mix Group (20K)") {
            distance = "20-mix";
            displayName = "Mix Group (20K)";
            categoryName = "Mix Group (20K)";
          } else if (cat.categoryName === "Short Distance (40K)") {
            distance = "40";
            displayName = "Short Distance (40K)";
          } else if (cat.categoryName === "Long Distance (80 K)") {
            distance = "80";
            displayName = "Long Distance (80 K)";
          }

          if (!seenDistances.has(distance)) {
            seenDistances.add(distance);
            mappedCategories.push({
              distance,
              displayName,
              description,
              categoryName,
            });
          }
        });

        const sortedCategories = [
          ...mappedCategories.filter((cat) => cat.distance === "7-vertical"),
          ...mappedCategories.filter((cat) => cat.distance === "7-fun"),
          ...mappedCategories.filter((cat) => cat.distance === "20-classic"),
          ...mappedCategories.filter((cat) => cat.distance === "20-mix"),
          ...mappedCategories.filter((cat) => cat.distance === "40"),
          ...mappedCategories.filter((cat) => cat.distance === "80"),
        ];

        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            categories: sortedCategories,
            categoriesLoaded: true,
            selectedCategory: sortedCategories.some(
              (cat) => cat.distance === prev.selectedCategory
            )
              ? prev.selectedCategory
              : sortedCategories[0]?.distance || "7-vertical",
          }));
        }
      } else {
        showNotification(
          "error",
          "Dokumen ULTRA24 tidak ditemukan. Menggunakan kategori default."
        );
      }
    } catch (err) {
      console.error("[ERROR] Failed to fetch categories:", err);
      showNotification("error", "Gagal memuat kategori. Menggunakan default.");
    }
  }, [showNotification]);

  const loadRaceData = useCallback(
    async (selectedDistance = state.selectedCategory) => {
      if (!state.categoriesLoaded || state.categories.length === 0) {
        console.warn("Categories not loaded yet, skipping loadRaceData");
        return;
      }

      setState((prev) => ({ ...prev, loading: true }));
      try {
        const bibNumberCache = await fetchAllBibNumbers();
        const subCollectionRef = collection(
          db,
          "resultsJOTR",
          selectedDistance,
          "results"
        );
        const resultsSnapshot = await getDocs(subCollectionRef);
        const category = state.categories.find(
          (cat) => cat.distance === selectedDistance
        );

        if (!category) {
          console.error(`Category not found for distance: ${selectedDistance}`);
          showNotification("error", "Kategori tidak ditemukan.");
          setState((prev) => ({ ...prev, loading: false }));
          return;
        }

        let resultsList = resultsSnapshot.docs.map((doc) => {
          const data = doc.data();
          const bibData = bibNumberCache.get(data.orderId) || {
            bibNumber: "-",
            teamName: data.teamName ? capitalizeWords(data.teamName) : null,
          };
          return {
            id: doc.id,
            ...data,
            categoryName: category.categoryName,
            bibNumber: bibData.bibNumber,
            teamName: bibData.teamName || data.teamName,
          };
        });

        // Urutkan hasil berdasarkan waktu untuk kategori 20-mix, berdasarkan ranking untuk kategori lain
        if (selectedDistance === "20-mix") {
          resultsList = resultsList
            .filter((result) => result.time && result.ranking !== "DNF")
            .sort((a, b) => timeToSeconds(a.time) - timeToSeconds(b.time))
            .map((result, index) => ({
              ...result,
              displayRanking: index + 1, // Tambahkan ranking berurutan berdasarkan waktu
            }))
            .concat(
              resultsList
                .filter((result) => !result.time || result.ranking === "DNF")
                .map((result) => ({
                  ...result,
                  displayRanking: "DNF", // Tetap gunakan DNF untuk yang tidak finish
                }))
            );
        } else {
          resultsList.sort((a, b) => {
            const rankA =
              a.ranking === "DNF" ? Infinity : parseInt(a.ranking) || Infinity;
            const rankB =
              b.ranking === "DNF" ? Infinity : parseInt(b.ranking) || Infinity;
            return rankA - rankB;
          });
          resultsList = resultsList.map((result) => ({
            ...result,
            displayRanking: result.ranking, // Gunakan ranking asli untuk kategori lain
          }));
        }

        let top3Male = [];
        let top3Female = [];
        if (selectedDistance === "20-mix") {
          top3Male = resultsList
            .filter((result) => result.ranking !== "DNF" && result.time)
            .slice(0, 3)
            .map((result, index) => ({
              ...result,
              ranking: index + 1,
            }));
        } else {
          top3Male = resultsList
            .filter(
              (result) =>
                result.gender === "M" && result.ranking !== "DNF" && result.time
            )
            .sort((a, b) => timeToSeconds(a.time) - timeToSeconds(b.time))
            .slice(0, 3)
            .map((result, index) => ({
              ...result,
              genderRanking: index + 1,
            }));

          top3Female = resultsList
            .filter(
              (result) =>
                result.gender === "F" && result.ranking !== "DNF" && result.time
            )
            .sort((a, b) => timeToSeconds(a.time) - timeToSeconds(b.time))
            .slice(0, 3)
            .map((result, index) => ({
              ...result,
              genderRanking: index + 1,
            }));
        }

        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            raceResults: resultsList,
            totalResults: resultsSnapshot.size,
            top3Male: selectedDistance === "20-mix" ? top3Male : top3Male,
            top3Female: selectedDistance === "20-mix" ? [] : top3Female,
            loading: false,
            bibNumberCache,
          }));
        }
      } catch (err) {
        console.error("[ERROR] Error fetching race data:", err);
        if (isMounted.current) {
          setState((prev) => ({ ...prev, loading: false }));
          showNotification(
            "error",
            "Gagal memuat hasil perlombaan. Silakan coba lagi."
          );
        }
      }
    },
    [
      showNotification,
      state.selectedCategory,
      state.categories,
      state.categoriesLoaded,
    ]
  );

  const normalizeString = (str) => {
    return str ? str.trim().replace(/\s+/g, " ").toLowerCase() : "";
  };

  const validateTimeFormat = (time) => {
    if (!time) return true;
    const regex = /^\d+:[0-5][0-9]:[0-5][0-9]$/;
    return regex.test(time);
  };

  const validateBirthdateFormat = (birthdate) => {
    if (!birthdate) return false;
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    return regex.test(birthdate);
  };

  const validateGender = (gender) => {
    if (!gender) return false;
    const normalizedGender = normalizeString(gender);
    const validGenders = ["m", "f", "male", "female", "laki-laki", "perempuan"];
    return validGenders.includes(normalizedGender);
  };

  const normalizeGender = (gender) => {
    const normalized = normalizeString(gender);
    if (["m", "male", "laki-laki"].includes(normalized)) return "M";
    if (["f", "female", "perempuan"].includes(normalized)) return "F";
    return null;
  };

  const checkUserRole = async (user) => {
    if (!user) return false;
    try {
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) return false;
      const userRole = userDoc.data().role;
      return userRole === "admin" || userRole === "rpc";
    } catch (err) {
      console.error("[ERROR] Error checking user role:", err);
      return false;
    }
  };

  const findMatchingParticipant = async (
    firstName,
    gender,
    birthdate,
    distance,
    bibNumberCache
  ) => {
    try {
      const participantsCollection = collection(db, "pendaftaranUltra24");
      const participantsSnapshot = await getDocs(participantsCollection);

      if (distance === "20-mix") {
        const matchingTeams = participantsSnapshot.docs.filter((doc) => {
          const data = doc.data();
          if (
            data.kategori !== "Mix Group (20K)" ||
            data.transaction_status !== "settlement"
          ) {
            return false;
          }
          const participants = data.participants || [];
          const isMatch = participants.some((participant) => {
            const namaDepanDb = normalizeString(participant.namaDepan || "");
            const namaDepanCsv = normalizeString(firstName);
            const genderDb = participant.gender === "Laki-laki" ? "M" : "F";
            return (
              (!skipNameValidation ? namaDepanDb === namaDepanCsv : true) &&
              genderDb === gender &&
              participant.tanggalLahir === birthdate
            );
          });
          return isMatch;
        });

        if (matchingTeams.length > 1) {
          console.warn(
            "[WARN] Multiple teams found for participant:",
            firstName,
            gender,
            birthdate
          );
        } else if (matchingTeams.length === 0) {
          console.warn(
            "[WARN] No team found for participant:",
            firstName,
            gender,
            birthdate
          );
        }
        return matchingTeams.length === 1
          ? {
              doc: matchingTeams[0],
              teamName: capitalizeWords(matchingTeams[0].data().teamName),
              bibNumber:
                bibNumberCache.get(matchingTeams[0].id)?.bibNumber || "-",
            }
          : null;
      } else {
        const matchingParticipants = participantsSnapshot.docs.filter((doc) => {
          const data = doc.data();
          const namaDepanDb = normalizeString(
            data.namaLengkap ? data.namaLengkap.split(" ")[0] : ""
          );
          const namaDepanCsv = normalizeString(firstName);
          const genderDb = data.gender === "Laki-laki" ? "M" : "F";
          return (
            data.transaction_status === "settlement" &&
            (!skipNameValidation ? namaDepanDb === namaDepanCsv : true) &&
            genderDb === gender &&
            data.tanggalLahir === birthdate
          );
        });

        if (matchingParticipants.length > 1) {
          console.warn(
            "[WARN] Multiple participants found for:",
            firstName,
            gender,
            birthdate
          );
        } else if (matchingParticipants.length === 0) {
          console.warn(
            "[WARN] No participant found for:",
            firstName,
            gender,
            birthdate
          );
        }
        return matchingParticipants.length === 1
          ? {
              doc: matchingParticipants[0],
              teamName: null,
              bibNumber:
                bibNumberCache.get(matchingParticipants[0].id)?.bibNumber ||
                "-",
            }
          : null;
      }
    } catch (err) {
      console.error("[ERROR] Error finding matching participant:", err);
      return null;
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const hasPermission = await checkUserRole(authUser);
    if (!hasPermission) {
      showNotification(
        "error",
        "Anda tidak memiliki izin untuk mengunggah hasil perlombaan."
      );
      return;
    }

    setCsvFileName(file.name);
    setCsvErrors([]);
    setCsvWarnings([]);
    setCsvSuccessCount(0);
    setCsvData([]);
    setSkipNameValidation(false);
    setClearExistingResults(false);
    setState((prev) => ({ ...prev, selectedDistance: null }));

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const data = result.data;
        if (!data.length) {
          showNotification("error", "CSV kosong atau tidak valid.");
          return;
        }

        const requiredHeaders = [
          "Ranking",
          "Time",
          "Family Name",
          "First Name",
          "Gender",
          "Birthdate",
          "Nationality",
        ];
        const headers = Object.keys(data[0]);
        const missingHeaders = requiredHeaders.filter(
          (header) => !headers.includes(header)
        );
        if (missingHeaders.length > 0) {
          showNotification(
            "error",
            `Header CSV tidak lengkap. Harus menyertakan: ${missingHeaders.join(
              ", "
            )}`
          );
          return;
        }

        setCsvData(data);
        setIsCsvModalOpen(true);
      },
      error: (err) => {
        console.error("[ERROR] Error parsing CSV:", err);
        showNotification(
          "error",
          "Gagal memproses file CSV. Pastikan formatnya benar."
        );
      },
    });
  };

  const handleDistanceChange = (e) => {
    setState((prev) => ({ ...prev, selectedDistance: e.target.value }));
  };

  const handleProcessCsv = async () => {
    if (!csvData.length) {
      showNotification("error", "Tidak ada data CSV untuk diproses.");
      return;
    }
    if (!state.selectedDistance) {
      showNotification("error", "Silakan pilih kategori perlombaan.");
      return;
    }

    const hasPermission = await checkUserRole(authUser);
    if (!hasPermission) {
      showNotification(
        "error",
        "Anda tidak memiliki izin untuk memproses hasil perlombaan."
      );
      return;
    }

    setIsProcessingCsv(true);
    setCsvErrors([]);
    setCsvWarnings([]);
    setCsvSuccessCount(0);
    const newErrors = [];
    const newWarnings = [];
    const resultsToSave = [];
    const participantSet = new Set();
    const bibNumberCache = await fetchAllBibNumbers();

    if (clearExistingResults) {
      await clearExistingRaceResults(state.selectedDistance);
    }

    if (state.selectedDistance === "20-mix") {
      const hasTeamNameColumn = csvData.some((row) => row["Team Name"]);
      const teams = [];

      if (hasTeamNameColumn) {
        const teamMap = new Map();
        for (const row of csvData) {
          const {
            Ranking: ranking,
            Time: time,
            "Family Name": familyName,
            "First Name": firstName,
            Gender: gender,
            Birthdate: birthdate,
            Nationality: nationality,
            "Team Name": teamName,
          } = row;

          if (!ranking) {
            newErrors.push(
              `Baris ${csvData.indexOf(row) + 2}: Ranking tidak valid.`
            );
            continue;
          }
          const isDNF = ranking.toUpperCase() === "DNF";
          const parsedRanking = isDNF ? "DNF" : parseInt(ranking);
          if (!isDNF && (isNaN(parsedRanking) || parsedRanking <= 0)) {
            newErrors.push(
              `Baris ${
                csvData.indexOf(row) + 2
              }: Ranking harus berupa angka positif atau DNF.`
            );
            continue;
          }
          if (time && !validateTimeFormat(time)) {
            newErrors.push(
              `Baris ${
                csvData.indexOf(row) + 2
              }: Format durasi tidak valid (harus HH:MM:SS).`
            );
            continue;
          }
          if (!firstName || typeof firstName !== "string") {
            newErrors.push(
              `Baris ${csvData.indexOf(row) + 2}: Nama Depan tidak valid.`
            );
            continue;
          }
          if (familyName && typeof familyName !== "string") {
            newErrors.push(
              `Baris ${csvData.indexOf(row) + 2}: Nama Belakang tidak valid.`
            );
            continue;
          }
          if (!validateGender(gender)) {
            newErrors.push(
              `Baris ${
                csvData.indexOf(row) + 2
              }: Gender harus M, F, Male, Female, Laki-laki, atau Perempuan.`
            );
            continue;
          }
          if (!validateBirthdateFormat(birthdate)) {
            newErrors.push(
              `Baris ${
                csvData.indexOf(row) + 2
              }: Format tanggal lahir tidak valid (harus YYYY-MM-DD).`
            );
            continue;
          }
          if (!nationality || typeof nationality !== "string") {
            newErrors.push(
              `Baris ${csvData.indexOf(row) + 2}: Nationality tidak valid.`
            );
            continue;
          }
          if (!teamName || typeof teamName !== "string") {
            newErrors.push(
              `Baris ${csvData.indexOf(row) + 2}: Team Name tidak valid.`
            );
            continue;
          }

          const normalizedGender = normalizeGender(gender);
          if (!normalizedGender) {
            newErrors.push(
              `Baris ${
                csvData.indexOf(row) + 2
              }: Gender "${gender}" tidak dapat dinormalisasi.`
            );
            continue;
          }

          if (!teamMap.has(teamName)) {
            teamMap.set(teamName, {
              ranking: parsedRanking,
              time: time || "",
              participants: [],
              orderId: null,
              teamName: capitalizeWords(teamName),
              categoryName: state.categories.find(
                (cat) => cat.distance === "20-mix"
              )?.categoryName,
              distance: "20-mix",
              createdAt: new Date(),
            });
          }
          teamMap.get(teamName).participants.push({
            firstName: capitalizeWords(firstName),
            familyName: capitalizeWords(familyName) || "",
            gender: normalizedGender,
            birthdate,
            nationality,
          });
        }

        for (const [teamName, team] of teamMap) {
          if (team.participants.length !== 3) {
            newErrors.push(
              `Tim "${teamName}" memiliki ${team.participants.length} anggota, harus 3 anggota.`
            );
            continue;
          }
          teams.push(team);
        }
      } else {
        for (let i = 0; i < csvData.length; i += 3) {
          const teamRows = csvData.slice(i, i + 3);
          if (teamRows.length < 3) {
            newErrors.push(
              `Baris ${i + 2} hingga ${
                i + teamRows.length + 1
              }: Tim tidak lengkap, hanya ada ${teamRows.length} anggota.`
            );
            continue;
          }

          const ranking = teamRows[0]["Ranking"];
          const time = teamRows[0]["Time"];
          const isDNF = ranking?.toUpperCase() === "DNF";
          const parsedRanking = isDNF ? "DNF" : parseInt(ranking);

          if (!ranking) {
            newErrors.push(`Baris ${i + 2}: Ranking tidak valid.`);
            continue;
          }
          if (!isDNF && (isNaN(parsedRanking) || parsedRanking <= 0)) {
            newErrors.push(
              `Baris ${i + 2}: Ranking harus berupa angka positif atau DNF.`
            );
            continue;
          }
          if (time && !validateTimeFormat(time)) {
            newErrors.push(
              `Baris ${i + 2}: Format durasi tidak valid (harus HH:MM:SS).`
            );
            continue;
          }

          const participants = [];
          for (const row of teamRows) {
            const {
              "Family Name": familyName,
              "First Name": firstName,
              Gender: gender,
              Birthdate: birthdate,
              Nationality: nationality,
            } = row;

            if (!firstName || typeof firstName !== "string") {
              newErrors.push(
                `Baris ${csvData.indexOf(row) + 2}: Nama Depan tidak valid.`
              );
              continue;
            }
            if (familyName && typeof familyName !== "string") {
              newErrors.push(
                `Baris ${csvData.indexOf(row) + 2}: Nama Belakang tidak valid.`
              );
              continue;
            }
            if (!validateGender(gender)) {
              newErrors.push(
                `Baris ${
                  csvData.indexOf(row) + 2
                }: Gender harus M, F, Male, Female, Laki-laki, atau Perempuan.`
              );
              continue;
            }
            if (!validateBirthdateFormat(birthdate)) {
              newErrors.push(
                `Baris ${
                  csvData.indexOf(row) + 2
                }: Format tanggal lahir tidak valid (harus YYYY-MM-DD).`
              );
              continue;
            }
            if (!nationality || typeof nationality !== "string") {
              newErrors.push(
                `Baris ${csvData.indexOf(row) + 2}: Nationality tidak valid.`
              );
              continue;
            }

            const normalizedGender = normalizeGender(gender);
            if (!normalizedGender) {
              newErrors.push(
                `Baris ${
                  csvData.indexOf(row) + 2
                }: Gender "${gender}" tidak dapat dinormalisasi.`
              );
              continue;
            }

            participants.push({
              firstName: capitalizeWords(firstName),
              familyName: capitalizeWords(familyName) || "",
              gender: normalizedGender,
              birthdate,
              nationality,
            });
          }

          if (participants.length === 3) {
            teams.push({
              ranking: parsedRanking,
              time: time || "",
              participants,
              orderId: null,
              teamName: `Tim Ranking ${parsedRanking}`,
              categoryName: state.categories.find(
                (cat) => cat.distance === "20-mix"
              )?.categoryName,
              distance: "20-mix",
              createdAt: new Date(),
            });
          }
        }
      }

      // Urutkan tim berdasarkan waktu untuk menetapkan ranking berurutan
      const sortedTeams = teams
        .filter((team) => team.time && team.ranking !== "DNF")
        .sort((a, b) => timeToSeconds(a.time) - timeToSeconds(b.time))
        .map((team, index) => ({
          ...team,
          ranking: index + 1, // Perbarui ranking berdasarkan urutan waktu
        }))
        .concat(
          teams
            .filter((team) => !team.time || team.ranking === "DNF")
            .map((team) => ({
              ...team,
              ranking: "DNF",
            }))
        );

      for (const team of sortedTeams) {
        let matchedTeam = null;
        for (const participant of team.participants) {
          const participantMatch = await findMatchingParticipant(
            participant.firstName,
            participant.gender,
            participant.birthdate,
            "20-mix",
            bibNumberCache
          );
          if (participantMatch) {
            matchedTeam = participantMatch;
            break;
          }
        }

        if (matchedTeam) {
          team.orderId = matchedTeam.doc.id;
          team.teamName = matchedTeam.teamName;
          team.bibNumber = matchedTeam.bibNumber;
        } else {
          newWarnings.push(
            `Tim "${team.teamName}" tidak ditemukan di pendaftaranUltra24. Disimpan sebagai UNKNOWN.`
          );
          team.orderId = "UNKNOWN";
          team.bibNumber = "-";
        }
        resultsToSave.push(team);
      }
    } else {
      for (const row of csvData) {
        const {
          Ranking: ranking,
          Time: time,
          "Family Name": familyName,
          "First Name": firstName,
          Gender: gender,
          Birthdate: birthdate,
          Nationality: nationality,
        } = row;

        if (!ranking) {
          newErrors.push(
            `Baris ${csvData.indexOf(row) + 2}: Ranking tidak valid.`
          );
          continue;
        }
        const isDNF = ranking.toUpperCase() === "DNF";
        const parsedRanking = isDNF ? "DNF" : parseInt(ranking);
        if (!isDNF && (isNaN(parsedRanking) || parsedRanking <= 0)) {
          newErrors.push(
            `Baris ${
              csvData.indexOf(row) + 2
            }: Ranking harus berupa angka positif atau DNF.`
          );
          continue;
        }
        if (time && !validateTimeFormat(time)) {
          newErrors.push(
            `Baris ${
              csvData.indexOf(row) + 2
            }: Format durasi tidak valid (harus HH:MM:SS).`
          );
          continue;
        }
        if (!firstName || typeof firstName !== "string") {
          newErrors.push(
            `Baris ${csvData.indexOf(row) + 2}: Nama Depan tidak valid.`
          );
          continue;
        }
        if (familyName && typeof familyName !== "string") {
          newErrors.push(
            `Baris ${csvData.indexOf(row) + 2}: Nama Belakang tidak valid.`
          );
          continue;
        }
        if (!validateGender(gender)) {
          newErrors.push(
            `Baris ${
              csvData.indexOf(row) + 2
            }: Gender harus M, F, Male, Female, Laki-laki, atau Perempuan.`
          );
          continue;
        }
        if (!validateBirthdateFormat(birthdate)) {
          newErrors.push(
            `Baris ${
              csvData.indexOf(row) + 2
            }: Format tanggal lahir tidak valid (harus YYYY-MM-DD).`
          );
          continue;
        }
        if (!nationality || typeof nationality !== "string") {
          newErrors.push(
            `Baris ${csvData.indexOf(row) + 2}: Nationality tidak valid.`
          );
          continue;
        }

        const normalizedGender = normalizeGender(gender);
        if (!normalizedGender) {
          newErrors.push(
            `Baris ${
              csvData.indexOf(row) + 2
            }: Gender "${gender}" tidak dapat dinormalisasi.`
          );
          continue;
        }

        const participantKey = `${firstName}-${familyName || ""}-${birthdate}`;
        if (participantSet.has(participantKey)) {
          newErrors.push(
            `Baris ${csvData.indexOf(row) + 2}: Peserta ${firstName} ${
              familyName || ""
            } dengan tanggal lahir ${birthdate} sudah ada dalam CSV.`
          );
          continue;
        }
        participantSet.add(participantKey);

        const participantMatch = await findMatchingParticipant(
          firstName,
          normalizedGender,
          birthdate,
          state.selectedDistance,
          bibNumberCache
        );
        let orderId = "UNKNOWN";
        let distance = state.selectedDistance;
        let participantCategoryName = null;
        let teamName = null;
        let bibNumber = "-";

        if (participantMatch) {
          const participantData = participantMatch.doc.data();
          orderId = participantMatch.doc.id;
          teamName = participantMatch.teamName;
          bibNumber = participantMatch.bibNumber;
          const regDistance = participantData.distance
            ? String(participantData.distance).trim()
            : null;
          participantCategoryName = participantData.kategori || null;
          distance = mapDistanceFromRegistration(
            regDistance,
            participantCategoryName
          );

          if (!participantCategoryName) {
            newWarnings.push(
              `Baris ${
                csvData.indexOf(row) + 2
              }: categoryName tidak ditemukan untuk peserta ${firstName} ${
                familyName || ""
              }. Menggunakan kategori dari modal.`
            );
          }
        } else {
          newWarnings.push(
            `Baris ${csvData.indexOf(row) + 2}: Peserta ${firstName} ${
              familyName || ""
            } tidak ditemukan di pendaftaranUltra24. Disimpan sebagai UNKNOWN.`
          );
        }

        if (
          !distance ||
          typeof distance !== "string" ||
          !state.categories.some((cat) => cat.distance === distance)
        ) {
          newErrors.push(
            `Baris ${
              csvData.indexOf(row) + 2
            }: Jarak ${distance} tidak valid atau tidak ditemukan di kategori.`
          );
          continue;
        }

        const category = state.categories.find(
          (cat) => cat.distance === distance
        );
        resultsToSave.push({
          orderId,
          ranking: parsedRanking,
          time: time || "",
          familyName: capitalizeWords(familyName) || "",
          firstName: capitalizeWords(firstName),
          gender: normalizedGender,
          birthdate,
          nationality,
          distance,
          categoryName: category.categoryName,
          teamName,
          bibNumber,
          createdAt: new Date(),
        });
      }
    }

    const rankingByDistance = new Map();
    for (const result of resultsToSave) {
      if (result.ranking !== "DNF") {
        const key = `${result.distance}-${result.ranking}`;
        if (rankingByDistance.has(key)) {
          newErrors.push(
            `Ranking ${result.ranking} sudah digunakan untuk jarak ${result.distance}.`
          );
        } else {
          rankingByDistance.set(key, result.orderId);
        }
      }
    }

    if (newErrors.length === 0 && resultsToSave.length > 0) {
      try {
        const resultsByDistance = new Map();
        resultsToSave.forEach((result) => {
          if (!result.distance) {
            newErrors.push(
              `Data tidak valid: Jarak tidak didefinisikan untuk ${
                result.teamName || result.firstName
              }.`
            );
            return;
          }
          if (!resultsByDistance.has(result.distance)) {
            resultsByDistance.set(result.distance, []);
          }
          resultsByDistance.get(result.distance).push(result);
        });

        if (newErrors.length > 0) throw new Error("Invalid distances found");

        for (const [distance, results] of resultsByDistance) {
          const subCollectionRef = collection(
            db,
            "resultsJOTR",
            distance,
            "results"
          );
          const existingResults = await getDocs(subCollectionRef);
          const existingParticipantKeys = new Set();
          const existingRankings = new Map();

          existingResults.forEach((doc) => {
            const data = doc.data();
            if (distance === "20-mix") {
              const key = data.teamName || `team-${doc.id}`;
              existingParticipantKeys.add(key);
            } else {
              const key = `${data.firstName}-${data.familyName || ""}-${
                data.birthdate
              }`;
              existingParticipantKeys.add(key);
            }
            if (data.ranking !== "DNF") {
              existingRankings.set(data.ranking, doc.id);
            }
          });

          for (const result of results) {
            const participantKey =
              distance === "20-mix"
                ? result.teamName || `team-${result.orderId}`
                : `${result.firstName}-${result.familyName || ""}-${
                    result.birthdate
                  }`;
            if (existingParticipantKeys.has(participantKey)) {
              newErrors.push(
                `${
                  distance === "20-mix" ? "Tim" : "Peserta"
                } "${participantKey}" sudah ada di subkoleksi ${
                  result.categoryName || distance
                }.`
              );
            }
          }
        }

        if (newErrors.length === 0) {
          const batch = writeBatch(db);
          resultsToSave.forEach((result) => {
            const resultRef = doc(
              collection(db, "resultsUltra24", result.distance, "results")
            );
            batch.set(resultRef, result);
          });
          await batch.commit();
          setCsvSuccessCount(resultsToSave.length);
          setState((prev) => ({
            ...prev,
            raceResults: [...prev.raceResults, ...resultsToSave],
            totalResults: prev.totalResults + resultsToSave.length,
            bibNumberCache,
          }));
          showNotification(
            "success",
            `Berhasil menyimpan ${resultsToSave.length} hasil perlombaan.${
              newWarnings.length > 0
                ? ` Ada ${newWarnings.length} peringatan.`
                : ""
            }`
          );
          setIsCsvModalOpen(false);
          setCsvData([]);
          setCsvFileName("");
          loadRaceData(state.selectedCategory);
        }
      } catch (err) {
        console.error("[ERROR] Error saving results:", err);
        newErrors.push("Gagal menyimpan hasil perlombaan ke database.");
      }
    }

    setCsvErrors(newErrors);
    setCsvWarnings(newWarnings);
    setIsProcessingCsv(false);

    if (newErrors.length > 0 || newWarnings.length > 0) {
      showNotification(
        "error",
        `Proses selesai. ${
          newErrors.length > 0
            ? `Ada ${newErrors.length} kesalahan kritis. `
            : ""
        }${
          newWarnings.length > 0 ? `Ada ${newWarnings.length} peringatan.` : ""
        }`
      );
    }
  };

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const handleCategoryChange = useCallback(
    debounce((distance) => {
      if (distance === state.selectedCategory) return;

      if (
        !state.categoriesLoaded ||
        !state.categories.some((cat) => cat.distance === distance)
      ) {
        console.warn(`Invalid or unloaded category: ${distance}`);
        showNotification("error", "Kategori belum dimuat atau tidak valid.");
        return;
      }

      setState((prev) => ({
        ...prev,
        selectedCategory: distance,
        raceResults: [],
        totalResults: 0,
        loading: true,
        top3Male: [],
        top3Female: [],
      }));
      loadRaceData(distance);
    }, 300),
    [
      state.selectedCategory,
      state.categories,
      state.categoriesLoaded,
      showNotification,
    ]
  );

  const handleSearchChange = useCallback(
    debounce((e) => {
      setState((prev) => ({ ...prev, searchQuery: e.target.value }));
      // Implement search logic here if needed, or let it filter the displayed results
    }, 300),
    []
  );

  const handleExportCSV = () => {
    // Placeholder for export logic
    showNotification("info", "Fungsi export CSV belum diimplementasikan.");
  };

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (state.categoriesLoaded && state.categories.length > 0) {
      loadRaceData(state.selectedCategory);
    }
  }, [
    state.categoriesLoaded,
    state.categories,
    state.selectedCategory,
    loadRaceData,
  ]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-gray-600">Memeriksa autentikasi...</p>
      </div>
    );
  }

  return (
    <section className="min-h-screen py-20 bg-gray-100">
      <div className="container mx-auto px-4">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">
          Unggah Hasil Perlombaan ULTRA24
        </h1>

        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {state.categories.map((category) => (
              <CategoryButton
                key={category.distance}
                category={category}
                selectedCategory={state.selectedCategory}
                onClick={handleCategoryChange}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleExportCSV}
              variant="warning"
              className="flex items-center gap-2"
              disabled={state.loading}
            >
              <FontAwesomeIcon icon={faDownload} />
              Export to CSV
            </Button>
            <Button
              variant="info"
              className="flex items-center gap-2"
              onClick={() => setIsCsvModalOpen(true)}
              disabled={state.loading}
            >
              <FontAwesomeIcon icon={faUpload} />
              Upload CSV
            </Button>
          </div>
        </div>

        <div className="mb-6 flex flex-col md:flex-row gap-4 items-end">
          <div className="w-full md:w-1/3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter Kategori
            </label>
            <select
              value={state.selectedCategory || ""}
              onChange={handleCategoryChange}
              className="w-full h-10 px-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-lime-500 transition-all duration-200"
              aria-label="Filter kategori hasil perlombaan"
            >
              <option value="">Semua Kategori</option>
              {state.categories.map((category) => (
                <option key={category.distance} value={category.distance}>
                  {category.displayName}
                </option>
              ))}
            </select>
          </div>
          <div className="w-full md:w-1/3">
            <InputField
              label="Cari Peserta"
              name="search"
              type="text"
              value={state.searchQuery}
              onChange={handleSearchChange}
              placeholder="Cari nama atau BIB..."
              startIcon={<FontAwesomeIcon icon={faSearch} className="text-gray-400" />}
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <Top3Table
            title={
              state.selectedCategory === "20-mix" ? "Top 3 Tim" : "Top 3 Pria"
            }
            results={state.top3Male}
            isMixGroup={state.selectedCategory === "20-mix"}
          />
          {state.selectedCategory !== "20-mix" && (
            <Top3Table
              title="Top 3 Wanita"
              results={state.top3Female}
              isMixGroup={false}
            />
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-300 overflow-x-auto mb-8">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ranking
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Durasi
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nomor Bib
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {state.selectedCategory === "20-mix"
                    ? "Nama Tim"
                    : "Nama Lengkap"}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {state.selectedCategory === "20-mix"
                    ? "Anggota Tim"
                    : "Gender"}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tanggal Lahir
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kebangsaan
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kategori
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {state.loading ? (
                <SkeletonRow />
              ) : state.raceResults.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-4 py-4 text-center text-sm text-gray-600"
                  >
                    Belum ada hasil perlombaan yang tersimpan untuk kategori
                    ini.
                  </td>
                </tr>
              ) : (
                state.raceResults.map((result, index) => (
                  <tr key={`${result.id || "unknown"}-${index}`}>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {result.orderId || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {result.displayRanking || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {result.time || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {result.bibNumber || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {state.selectedCategory === "20-mix"
                        ? capitalizeWords(result.teamName) || "-"
                        : capitalizeWords(
                            `${result.firstName} ${result.familyName || ""}`
                          ) || "-"}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      {state.selectedCategory === "20-mix"
                        ? result.participants
                          ? result.participants
                              .map((p) =>
                                capitalizeWords(
                                  `${p.firstName} ${p.familyName || ""}`
                                )
                              )
                              .join(", ")
                          : "-"
                        : result.gender || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {state.selectedCategory === "20-mix"
                        ? result.participants
                          ? result.participants
                              .map((p) => p.birthdate)
                              .join(", ")
                          : "-"
                        : result.birthdate || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {state.selectedCategory === "20-mix"
                        ? result.participants
                          ? result.participants
                              .map((p) => p.nationality)
                              .join(", ")
                          : "-"
                        : result.nationality || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {result.categoryName || "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {isCsvModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg py-6 px-4 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Unggah Hasil Perlombaan via CSV
              </h3>
              {csvFileName && (
                <p className="text-sm text-gray-600 mb-2">
                  File: {csvFileName}
                </p>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pilih Kategori Perlombaan
                </label>
                <select
                  value={state.selectedDistance || ""}
                  onChange={handleDistanceChange}
                  className="w-full h-10 px-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-lime-500 transition-all duration-200"
                  aria-label="Pilih kategori perlombaan"
                >
                  <option value="" disabled>
                    Pilih kategori
                  </option>
                  {state.categories.map((category) => (
                    <option key={category.distance} value={category.distance}>
                      {category.displayName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={skipNameValidation}
                    onChange={(e) => setSkipNameValidation(e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-yellow-800">
                    Lewati validasi nama depan
                  </span>
                </label>
                <p className="text-xs text-yellow-700 mt-1">
                  Gunakan ini jika nama depan di CSV berbeda format dengan
                  database.
                </p>
              </div>
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={clearExistingResults}
                    onChange={(e) => setClearExistingResults(e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-red-800">
                    Hapus hasil lama untuk kategori ini sebelum unggah
                  </span>
                </label>
                <p className="text-xs text-red-700 mt-1">
                  Centang ini untuk menghapus semua hasil lama di kategori yang
                  dipilih sebelum menyimpan data baru.
                </p>
              </div>
              {csvData.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-md font-semibold text-gray-800 mb-2">
                    Pratinjau Data
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse border border-gray-200">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border border-gray-200 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Baris
                          </th>
                          <th className="border border-gray-200 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Ranking
                          </th>
                          <th className="border border-gray-200 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Durasi
                          </th>
                          <th className="border border-gray-200 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Nama Depan
                          </th>
                          <th className="border border-gray-200 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Nama Belakang
                          </th>
                          <th className="border border-gray-200 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Gender
                          </th>
                          <th className="border border-gray-200 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Tanggal Lahir
                          </th>
                          <th className="border border-gray-200 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Kebangsaan
                          </th>
                          {csvData.some((row) => row["Team Name"]) && (
                            <th className="border border-gray-200 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Nama Tim
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {csvData.map((row, index) => (
                          <tr
                            key={`csv-row-${index}`}
                            className="hover:bg-gray-50"
                          >
                            <td className="border border-gray-200 px-4 py-2 text-sm text-gray-600">
                              {index + 2}
                            </td>
                            <td className="border border-gray-200 px-4 py-2 text-sm text-gray-600">
                              {row["Ranking"] || "-"}
                            </td>
                            <td className="border border-gray-200 px-4 py-2 text-sm text-gray-600">
                              {row["Time"] || "-"}
                            </td>
                            <td className="border border-gray-200 px-4 py-2 text-sm text-gray-600">
                              {capitalizeWords(row["First Name"]) || "-"}
                            </td>
                            <td className="border border-gray-200 px-4 py-2 text-sm text-gray-600">
                              {capitalizeWords(row["Family Name"]) || "-"}
                            </td>
                            <td className="border border-gray-200 px-4 py-2 text-sm text-gray-600">
                              {row["Gender"] || "-"}
                            </td>
                            <td className="border border-gray-200 px-4 py-2 text-sm text-gray-600">
                              {row["Birthdate"] || "-"}
                            </td>
                            <td className="border border-gray-200 px-4 py-2 text-sm text-gray-600">
                              {row["Nationality"] || "-"}
                            </td>
                            {csvData.some((row) => row["Team Name"]) && (
                              <td className="border border-gray-200 px-4 py-2 text-sm text-gray-600">
                                {capitalizeWords(row["Team Name"]) || "-"}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {csvErrors.length > 0 && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                  <h4 className="text-md font-semibold text-red-800 mb-2">
                    Kesalahan Kritis ({csvErrors.length})
                  </h4>
                  <ul className="list-disc list-inside text-sm text-red-700">
                    {csvErrors.map((error, index) => (
                      <li key={`error-${index}`}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
              {csvWarnings.length > 0 && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <h4 className="text-md font-semibold text-yellow-800 mb-2">
                    Peringatan ({csvWarnings.length})
                  </h4>
                  <ul className="list-disc list-inside text-sm text-yellow-700">
                    {csvWarnings.map((warning, index) => (
                      <li key={`warning-${index}`}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
              {csvSuccessCount > 0 && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
                  <p className="text-sm text-green-700">
                    Berhasil memproses {csvSuccessCount} baris.
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-4">
                <Button
                  onClick={() => {
                    setIsCsvModalOpen(false);
                    setCsvData([]);
                    setCsvErrors([]);
                    setCsvWarnings([]);
                    setCsvSuccessCount(0);
                    setCsvFileName("");
                    setState((prev) => ({ ...prev, selectedDistance: null }));
                    setClearExistingResults(false);
                  }}
                  variant="secondary"
                  disabled={isProcessingCsv}
                >
                  Batal
                </Button>
                <Button
                  onClick={handleProcessCsv}
                  variant="primary"
                  disabled={isProcessingCsv || !state.selectedDistance}
                >
                  {isProcessingCsv ? "Memproses..." : "Proses CSV"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
